import { endpointsList } from '@/helpers';
import axios from 'axios';
import {
  IKksTreeRequestDto,
  IKksTreeResponseDto,
  IKksValidationResultDto,
  IPreselectedNodesRequestDto,
  ITreeSelectKKS,
  IValidateKksListDto,
  KksState,
  SlVueTreeSearchOptions,
} from 'types';
import { removeFalsyProperties } from 'utils';

export class KksTreeService {
  public static async getKksTree(params: {
    locationScope: string;
    branchNode?: string | null;
    searchTerm?: string;
    offset?: number;
    limit?: number;
    searchMode?: SlVueTreeSearchOptions | null;
    state?: KksState[];
    workspaceId?: string;
  }): Promise<IKksTreeResponseDto> {
    const dto: IKksTreeRequestDto = {
      locationScope: params.locationScope,
      branchNode: params.branchNode,
      searchTerm: params.searchTerm,
      offset: params.offset,
      limit: params.limit,
      searchMode: params.searchMode,
      state: params.state,
    };
    const filteredDto = removeFalsyProperties(dto);

    if (params.workspaceId) {
      const response = await axios.get<IKksTreeResponseDto>(
        endpointsList.workspaces.getKksTreeForWorkspaceCoworker(
          params.workspaceId,
        ),
        { params: filteredDto },
      );
      return response.data;
    }

    const response = await axios.get<IKksTreeResponseDto>(
      endpointsList.prodisApiIntegration.getKksTree(),
      { params: filteredDto },
    );
    return response.data;
  }

  public static async getPreselectedKksNodes(
    kks: string[],
    location: string,
  ): Promise<ITreeSelectKKS[]> {
    const body: IPreselectedNodesRequestDto = {
      nodes: kks,
      location,
    };
    const response = await axios.post<ITreeSelectKKS[]>(
      endpointsList.prodisApiIntegration.getPreselectedNodes(),
      body,
    );
    const existingKksNodes = response.data;
    const notExistingKks = kks
      .filter((kks) => !existingKksNodes.some((e) => e.kks_trimmed === kks))
      .map((kks) => this.toKksNode(kks));

    const validationReqBody: IValidateKksListDto = {
      kksList: notExistingKks.map((n) => n.kks_trimmed),
      location,
    };
    const notExistingValidation = await axios.post<IKksValidationResultDto[]>(
      endpointsList.prodisApiIntegration.validateKksList(),
      validationReqBody,
    );
    return [
      ...existingKksNodes,
      ...notExistingKks.map((n) => {
        const isValid = notExistingValidation.data.find(
          (v) => v.kks === n.kks_trimmed,
        )?.isValid;
        /**
         * - DMS_PROPOSED KKS (valid, not-existing) have `warning` color in the
         *   tree
         * - NON_CONFORMING KKS (invalid, not-existing) have `danger` color in
         *   the tree
         */
        const state = isValid ? KksState.DMS_PROPOSED : KksState.NON_CONFORMING;
        return {
          ...n,
          isValid,
          state,
        };
      }),
    ];
  }

  public static toKksNode(kks: string): ITreeSelectKKS {
    return {
      _id: kks,
      kks_trimmed: kks,
      state: KksState.DDP_PROPOSED,
      childStates: {},
    };
  }
}
